<template>
  <article
    :class="[
      'mm-material-tile group relative overflow-hidden border border-neutral-200 ',
      viewMode === 'grid' ? 'flex flex-col h-full' : 'flex items-stretch gap-0 h-[120px] sm:h-[200px]'
    ]"
  >
    <!-- Maak hele kaart aanklikbaar met overlay link -->
    <a
      :href="detailHref"
      class="absolute inset-0 z-10"
      :aria-label="(itemTitle || '') + ' – ' + (t('materials.viewMaterial') || 'Bekijk materiaal')"
      tabindex="0"
    ></a>


    <!-- Image area -->
    <div
      :class="[
        'relative shrink-0 overflow-hidden',
        viewMode === 'grid' ? 'aspect-square sm:aspect-[4/3] w-full' : 'w-[100px] sm:w-[250px]'
      ]"
    >
      <img :src="item.image || placeholderImg" :alt="itemTitle" class="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"/>
      
      <!-- Provider badge -->
      <div v-if="isFromSchool || isFromCompany" class="absolute top-3 left-3 z-20 flex flex-col gap-1 items-start">
        <span class="bg-black text-white px-2 py-1 text-[10px] font-black uppercase italic tracking-wider">
          {{ isFromSchool ? (t('materials.fromSchool') || 'School') : (t('materials.fromCompany') || 'Bedrijf') }}
        </span>
      </div>

      <!-- Views overlay -->
      <div class="pointer-events-none absolute bottom-3 left-3 flex items-center gap-1.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)]">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 20.571 16">
          <path id="eye-sharp-regular" d="M4.611,43.764A13.956,13.956,0,0,1,1.875,40a13.8,13.8,0,0,1,2.736-3.764,8.158,8.158,0,0,1,5.675-2.521,8.158,8.158,0,0,1,5.675,2.521A13.856,13.856,0,0,1,18.7,40a13.8,13.8,0,0,1-2.736,3.764,8.158,8.158,0,0,1-5.675,2.521A8.158,8.158,0,0,1,4.611,43.764ZM10.286,48c5.657,0,9.214-5.332,10.286-8-1.071-2.668-4.629-8-10.286-8S1.071,37.332,0,40C1.071,42.668,4.629,48,10.286,48Zm0-5.143A2.856,2.856,0,0,1,7.429,40a2.9,2.9,0,0,1,.054-.561l2.8.561-.561-2.8a2.9,2.9,0,0,1,.561-.054,2.857,2.857,0,0,1,0,5.714ZM5.714,40a4.571,4.571,0,1,0,4.571-4.571,4.362,4.362,0,0,0-.9.089,4.578,4.578,0,0,0-3.582,3.561l0,.025a4.624,4.624,0,0,0-.089.9Z" transform="translate(0 -32)" fill="white"/>
        </svg>
        <span class="text-sm font-bold">{{ item.views ?? 0 }}</span>
      </div>

      <!-- Diagonaal label voor reeds geclaimde producten -->
      <div v-if="showClaimedLabel" class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-12 z-20 w-[140%] text-center">
        <span class="inline-block bg-[#E24C3B] text-white px-4 py-1.5 text-xs sm:text-sm font-black uppercase tracking-widest shadow-lg select-none">
          {{ (t('materials.tooLate') || 'Te laat').toUpperCase() }}
        </span>
      </div>
    </div>

    <!-- Content area -->
    <div :class="['flex flex-col flex-1 min-w-0 p-1.5 sm:p-4', viewMode === 'grid' ? 'justify-start' : 'justify-between']">
      <header>
        <h4 class="mm-material-tile-title !text-[18px] !leading-[21px] sm:!text-[20px] sm:!leading-tight pb-5">
          {{ itemTitle }}
        </h4>
        <p v-if="viewMode === 'list'" class="mt-0.5 line-clamp-2 break-words">
          {{ teaserText }}
        </p>
      </header>

      <div :class="['mm-material-tile-meta mt-auto flex items-center gap-1.5', viewMode === 'grid' ? 'pt-1' : 'pt-2']">
        <!-- Categorie icoon -->
        <div class="flex shrink-0 relative z-20">
          <a
            v-if="overviewCategoryHref"
            :href="overviewCategoryHref"
            class="mm-material-tile-category inline-flex h-5 w-5 sm:h-8 sm:w-8 items-center justify-center overflow-hidden rounded-full bg-white "
            :title="item?.category?.name"
            @click.stop
          >
            <img v-if="item?.category?.imageUrl" :src="item.category.imageUrl" :alt="item?.category?.name" class="h-full w-full object-cover"/>
            <img v-else :src="catPlaceholder" class="h-full w-full object-cover"/>
          </a>
          <span v-else class="mm-material-tile-category inline-flex h-5 w-5 sm:h-8 sm:w-8 items-center justify-center overflow-hidden rounded-full bg-white ">
            <img v-if="item?.category?.imageUrl" :src="item.category.imageUrl" class="h-full w-full object-cover"/>
            <img v-else :src="catPlaceholder" class="h-full w-full object-cover"/>
          </span>
        </div>

        <!-- Prijs chip -->
        <span v-if="!isRequested && item.price != null && Number(item.price) !== 0 && !Number.isNaN(Number(item.price))" class="mm-material-tile-price flex overflow-hidden whitespace-nowrap items-center rounded-full  bg-white text-[10px] sm:text-sm text-black relative z-20">
          <span class="shrink-0 px-2 sm:px-3 py-0.5 sm:py-1 font-bold sm:font-semibold  text-black">t.w.v. € {{ priceText(item.price) }}</span>
        </span>

        <!-- Type chip -->
        <span v-if="isRequested" class="mm-material-tile-type bg-black text-white px-3 py-1 text-[11px] font-black uppercase italic tracking-wider rounded-full relative z-20">
          {{ t('materials.requested') || 'Gevraagd' }}
        </span>
        <span v-else class="mm-material-tile-type bg-[#E7D7BF] text-black px-3 py-1 text-[11px] font-black uppercase italic tracking-wider rounded-full relative z-20">
          {{ t('materials.offered') || 'Aangeboden' }}
        </span>


        <!-- View details button -->
        <div class="mm-material-tile-view-details ml-auto">
           <button type="button" @click="goTo" :aria-label="t('materials.viewMaterial') || 'Bekijk materiaal'" class="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center border border-black transition-colors hover:bg-black hover:text-white relative z-20">
             <svg xmlns="http://www.w3.org/2000/svg" width="19.058" height="16" viewBox="0 0 19.058 16"
                  class="transition-colors duration-300">
               <path id="arrow-right-sharp-regular"
                     d="M18.333,77.127l.725-.725-.725-.725-6.552-6.552-.725-.725L9.61,69.846l.725.725,4.8,4.807H0v2.047H15.138l-4.807,4.8-.725.725L11.052,84.4l.725-.725Z"
                     transform="translate(0 -68.4)" fill="currentColor"/>
             </svg>
           </button>
        </div>
      </div>
    </div>
  </article>

  <!-- Waarde toelichting modal -->
  <MmModal v-model="openValueInfo" :title="t('materials.valueInfoTitle') || 'Waarde toelichting'">
    <p>
      {{ t('materials.valueInfoBody') || 'De materialen worden GRATIS aangeboden door bedrijven. We vermelden alleen dit bedrag om aan te geven voor welke waarde het vertegenwoordigt.' }}
    </p>
  </MmModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { t as i18nT } from '../i18n'
import MmModal from './MmModal.vue'

const t = (key) => i18nT(key)

// Placeholder image used for category avatars when no image is available
const catPlaceholder = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#e5e7eb"/><circle cx="20" cy="20" r="12" fill="#d1d5db"/></svg>`)

const props = defineProps({
  item: { type: Object, required: true },
  placeholderImg: { type: String, default: '' },
  detailHref: { type: String, default: '#' },
  // Toon statusbadge alleen wanneer expliciet gevraagd (bijv. in admin). Publiek: standaard false.
  showStatus: { type: Boolean, default: false },
  viewMode: { type: String, default: 'list' }
})

function decodeHTML(str) {
  if (!str || typeof str !== 'string' || !str.includes('&')) return str || ''
  try {
    const doc = new DOMParser().parseFromString(str, 'text/html')
    return doc.documentElement.textContent || str
  } catch { return str }
}

const itemTitle = computed(() => {
  const raw = props.item.title || props.item.name || ''
  return decodeHTML(raw)
})
// Teasertekst: eerste ~140 tekens uit de beschrijving (samenvattingsveld vervalt)
const teaserText = computed(() => {
  const raw = String(props.item?.description || props.item?.excerpt || '').trim()
  if (!raw) return ''
  // strip eventuele HTML tags
  let plain = raw.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
  // decode HTML entities
  plain = decodeHTML(plain)
  if (plain.length <= 140) return plain
  return plain.slice(0, 140).replace(/\s+\S*$/, '') + '…'
})
// Lees locatie uit meerdere mogelijke velden zodat het ook werkt als de REST-meta niet is blootgesteld
const locationText = computed(() => props.item.location || props.item.locationText || props.item.mm_locatie || '')
const statusKey = computed(() => {
  const k = String(props.item.statusKey || props.item.mm_claim?.status || '').toLowerCase()
  return k
})
const statusLabel = computed(() => props.item.statusLabel || (statusKey.value === 'concept' ? 'Concept' : statusKey.value === 'archief' ? 'Archief' : statusKey.value === 'geclaimd' ? 'Geclaimd' : statusKey.value === 'active' ? 'Actief' : ''))
const statusClass = computed(() => {
  const k = statusKey.value
  if (k === 'geclaimd') return 'bg-[#3f873f] text-white'
  if (k === 'concept') return 'bg-[#fff3cd] text-[#664d03] border border-[#ffecb5]'
  if (k === 'archief') return 'bg-neutral-200 text-neutral-800'
  if (k === 'active') return 'bg-white text-black border border-black'
  return 'bg-white text-black border border-black'
})
// Publieke claimed indicator (zonder admin status badge)
const isClaimed = computed(() => {
  if (props.item && typeof props.item.claimed !== 'undefined') return !!props.item.claimed
  const claim = props.item?.mm_claim || null
  const hasActiveClaim = !!(claim && Number(claim.claimed_by_user || 0) !== 0 && String(claim.status || '').toLowerCase() !== 'cancelled')
  return statusKey.value === 'geclaimd' || hasActiveClaim
})
const showClaimedLabel = computed(() => props.showStatus && isClaimed.value)
const isFromSchool = computed(() => {
  const role = String(props.item.mm_author_role || props.item.author_role || props.item.provider?.role || '').toLowerCase()
  return role === 'school'
})
const isFromCompany = computed(() => {
  const role = String(props.item.mm_author_role || props.item.author_role || props.item.provider?.role || '').toLowerCase()
  return role === 'bedrijf'
})
const isRequested = computed(() => {
  const type = String(props.item.mm_type || props.item.type || '').toLowerCase()
  return type === 'gevraagd' || type === 'requested'
})
const locationHref = computed(() => {
  const txt = locationText.value?.trim()
  if (!txt) return null
  // Prefer precomputed URL if provided
  if (props.item.locationUrl) return props.item.locationUrl
  try {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(txt)}`
  } catch { return null }
})

function getMaterialsBasePath(){
  try {
    const path = window.location.pathname.replace(/\/+$/, '') || '/'
    const parts = path.split('/').filter(Boolean)
    const idx = parts.indexOf('materialen')
    if (idx !== -1) return '/' + parts.slice(0, idx + 1).join('/')
  } catch (_) {}
  return '/materialen'
}

const overviewLocationHref = computed(() => {
  const txt = locationText.value?.trim()
  if (!txt) return null
  try {
    const url = new URL(window.location.href)
    url.pathname = getMaterialsBasePath()
    const p = url.searchParams
    p.set('location', txt)
    p.delete('page')
    return url.toString()
  } catch {
    return `/materialen?location=${encodeURIComponent(txt)}`
  }
})

// Link naar overzicht met geselecteerde categorie actief (pretty path met slug, fallback naar ?cat=id)
const overviewCategoryHref = computed(() => {
  try {
    const cat = (props.item && props.item.category) ? props.item.category : null
    if (!cat || (!cat.slug && !cat.id)) return null
    const base = getMaterialsBasePath()
    if (cat.slug) return `${base}/${cat.slug}`
    if (cat.id) return `${base}?cat=${encodeURIComponent(String(cat.id))}`
    return null
  } catch {
    return null
  }
})

function priceText(p){ return (p != null && !Number.isNaN(Number(p))) ? Number(p).toFixed(0) : '—' }
function initials(name) { 
  if (!name || typeof name !== 'string') return ''
  try {
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase()
  } catch (e) {
    return ''
  }
}
function host(url) { 
  if (!url) return ''
  try { return new URL(url).host } catch { return String(url) } 
}
// Geen padId meer nodig in publieke overzichten

// Modal state
const openValueInfo = ref(false)

function goTo(){
  const href = String(props.detailHref || '#')
  if (!href || href === '#') return
  try { window.location.assign(href) }
  catch { window.location.href = href }
}
</script>

<style scoped>
.clamp-2{
  display:-webkit-box;
  -webkit-line-clamp:2;
  -webkit-box-orient:vertical;
  overflow:hidden;
}
</style>
